import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  HStack,
  Link,
  List,
  Spacer,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { RiDraggable } from "@remixicon/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDragControls } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { Link as RouterLink, useNavigate } from "react-router";

import { Restricted } from "@/app/components/Restricted";
import {
  DeleteIconButton,
  EditIconButton,
} from "@/common/components/CommonIconButtons";
import FloatingFormError from "@/common/components/Form/FloatingFormError";
import FormCancel from "@/common/components/Form/FormCancel";
import FormInput from "@/common/components/Form/FormInput";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import QueryDataGuard from "@/common/components/QueryDataGuard";
import { ReorderList } from "@/common/components/ReorderList";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { commonQueryKeys } from "@/common/data/commonQueryKeys";
import useApiForm from "@/common/hooks/useApiForm";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import api from "@/common/utils/api";
import { EnvironmentDisplayName } from "@/environment/components/EnvironmentDisplayName";
import { environmentQueryKeys } from "@/environment/data/environmentQueryKeys";
import { useEnvironmentUpdateMutation } from "@/environment/data/useEnvironmentsUpdateMutation";
import { useEnvironments } from "@/environment/hooks/useEnvironments";
import {
  ListItem,
  ListLayout,
  ListLayoutProps,
} from "@/global-settings/components/GlobalSettingsHelpers";
import { ErrorResponse } from "@reflag/shared/api";
import {
  EnvironmentHeaderDTO,
  EnvironmentPatchArgs,
  EnvironmentPatchSchema,
  EnvironmentPostSchema,
} from "@reflag/shared/environmentAPI";
import { GlobalSettingsUrl } from "@reflag/shared/urls";
import { z } from "@reflag/shared/utils/zod";

const Layout = (props: ListLayoutProps) => (
  <ListLayout templateColumns="1fr auto" {...props} />
);

type ItemProps = {
  production?: EnvironmentHeaderDTO;
  environment: EnvironmentHeaderDTO;
  canDrag?: boolean;
  canEdit?: boolean;
};

function Item({ production, environment, canDrag, canEdit }: ItemProps) {
  const toast = useToast();
  const errorToast = useErrorToast();

  const { appId, orgId } = useEnvironment();
  const queryClient = useQueryClient();

  const dragControls = useDragControls();

  const { form, handleSubmit } = useApiForm(
    (data: EnvironmentPatchArgs) =>
      api
        .patch<"/apps/:appId/environments/:envId">(
          `/apps/${appId}/environments/${environment.id}`,
          data,
        )
        .then((res) => res.data),
    EnvironmentPatchSchema,
    {
      onSuccess: async (environment) => {
        await queryClient.invalidateQueries({
          queryKey: commonQueryKeys.bootstrap(orgId),
        });
        await queryClient.invalidateQueries({
          queryKey: environmentQueryKeys.list(appId),
        });
        toast({
          title: "Environment updated",
          status: "success",
        });
        form.reset({
          name: environment.name,
        });
        setShowEditForm(false);
      },
      onError: (error) => {
        errorToast({
          title: `Failed to update '${environment.name}' environment`,
          description: (error.response?.data as ErrorResponse | null)?.error
            .message,
        });
      },
    },
    {
      defaultValues: {
        name: environment.name,
      },
    },
  );

  const [showEditForm, setShowEditForm] = useState(false);

  return !showEditForm ? (
    <ListItem
      canDrag={canDrag}
      dragControls={dragControls}
      value={environment}
      gridTemplateColumns="1fr auto"
    >
      {canDrag && (
        <Box
          boxSize={3}
          left={-6}
          position="absolute"
          sx={{
            cursor: "grab",
            "&:active": {
              cursor: "grabbing",
            },
          }}
          onPointerDown={(e) => {
            if (!canDrag) return;
            dragControls.start(e);
          }}
        >
          <RiDraggable size={16} />
        </Box>
      )}
      <HStack>
        <EnvironmentDisplayName environment={environment} fontSize="md" />
        {environment.isProduction && (
          <Text color="dimmed" fontSize="sm" fontStyle="italic">
            Default, cannot be changed
          </Text>
        )}
      </HStack>
      <Box>
        {canEdit && (
          <>
            <EditIconButton
              label="Edit environment"
              onClick={() => setShowEditForm?.(true)}
            />
            <DeleteEnvironment
              environment={environment}
              production={production}
            />
          </>
        )}
      </Box>
    </ListItem>
  ) : (
    <FormProvider {...form}>
      <EnvironmentForm
        submitLabel="Save"
        onClose={() => {
          setShowEditForm(false);
          form.reset();
        }}
        onSubmit={handleSubmit}
      />
    </FormProvider>
  );
}

function DeleteEnvironment({
  production,
  environment,
}: {
  production?: EnvironmentHeaderDTO;
  environment: EnvironmentHeaderDTO;
}) {
  const navigate = useNavigate();
  const { app, orgId } = useEnvironment();
  const toast = useToast();
  const queryClient = useQueryClient();
  const errorToast = useErrorToast();

  const { mutate: deleteEnvironment, isPending: isDeleteLoading } = useMutation(
    {
      mutationFn: () =>
        api.delete<"/apps/:appId/environments/:envId">(
          `/apps/${app.id}/environments/${environment.id}`,
        ),

      retry: 0,

      onSuccess: async () => {
        if (production) {
          navigate(GlobalSettingsUrl(production, "app-environments"));
        }

        await queryClient.invalidateQueries({
          queryKey: commonQueryKeys.bootstrap(orgId),
        });
        await queryClient.invalidateQueries({
          queryKey: environmentQueryKeys.list(app.id),
        });

        toast({
          title: "Deleted environment",
          status: "success",
        });
      },

      onError: () => {
        errorToast({
          title: `Failed to delete '${environment.name}' environment`,
        });
      },
    },
  );

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef(null);

  return (
    <>
      <DeleteIconButton
        isDisabled={isDeleteLoading}
        label="Delete environment"
        onClick={onOpen}
      />
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete environment</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete{" "}
              <strong>{environment.name}</strong> ?
              <br />
              <br />
              All flags using this environment will revert to the default Active
              environment. You can&apos;t undo this action afterwards.
            </AlertDialogBody>
            <AlertDialogFooter>
              <ButtonGroup>
                <Button
                  ref={cancelRef}
                  isDisabled={isDeleteLoading}
                  variant="ghostDimmed"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  isDisabled={isDeleteLoading}
                  isLoading={isDeleteLoading}
                  onClick={() => deleteEnvironment()}
                >
                  Delete
                </Button>
              </ButtonGroup>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

function EnvironmentsList({
  production,
  nonProduction: nonProductionProp,
}: {
  production: EnvironmentHeaderDTO;
  nonProduction: EnvironmentHeaderDTO[];
}) {
  const [nonProduction, setNonProduction] = useState(nonProductionProp);
  const updateMutation = useEnvironmentUpdateMutation();

  useEffect(() => {
    setNonProduction(nonProductionProp);
  }, [nonProductionProp]);

  if (!production && !nonProduction.length) {
    return (
      <Alert status="info">
        <AlertIcon />
        <AlertDescription>
          This application has no environments
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <Layout minH="auto" py={1} showAsHeader>
        <Box>Name</Box>
      </Layout>
      {/* Static list of production environments */}
      <List display="flex" flexDir="column" gap={2}>
        <Item key={production.id} environment={production} />
      </List>
      {/* Reorderable list of nonproduction environments */}
      <ReorderList
        axis="y"
        display="flex"
        flexDir="column"
        gap={2}
        values={nonProduction}
        onDrop={(environment, order) => {
          updateMutation.mutateAsync({
            id: environment.id,
            order: order + 1, // 1-based index due to production being the first
          });
        }}
        onReorder={setNonProduction}
      >
        {nonProduction.map((environment) => {
          return (
            <Item
              key={environment.id}
              environment={environment}
              production={production}
              canDrag
              canEdit
            />
          );
        })}
      </ReorderList>
    </>
  );
}

function EnvironmentForm({
  onClose,
  onSubmit,
  submitLabel,
}: {
  onClose: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  submitLabel: string;
}) {
  return (
    <form onSubmit={onSubmit}>
      <Layout py={4} templateColumns="1fr 1fr 64px" showAsCard>
        <Box>
          <FloatingFormError name="name">
            <Box>
              <FormInput
                autoComplete="off"
                bg="appBackground"
                name="name"
                placeholder="Name your environment"
                showErrors={false}
                autoFocus
                data-1p-ignore
              />
            </Box>
          </FloatingFormError>
        </Box>
        <Spacer />
        <Box justifySelf="end">
          <FormRootError />
          <ButtonGroup>
            <FormCancel onClick={() => onClose()} />
            <FormSubmit>{submitLabel}</FormSubmit>
          </ButtonGroup>
        </Box>
      </Layout>
    </form>
  );
}

function CreateEnvironment() {
  const { app, orgId } = useEnvironment();
  const toast = useToast();
  const errorToast = useErrorToast();
  const queryClient = useQueryClient();

  const { form, handleSubmit } = useApiForm(
    (data: z.input<typeof EnvironmentPostSchema>) =>
      api
        .post<"/apps/:appId/environments">(`/apps/${app.id}/environments`, data)
        .then((res) => res.data),
    EnvironmentPostSchema,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: commonQueryKeys.bootstrap(orgId),
        });
        await queryClient.invalidateQueries({
          queryKey: environmentQueryKeys.list(app.id),
        });
        toast({
          title: "Environment created",
          status: "success",
        });
        form.reset({
          name: "",
        });
        setShowCreateForm(false);
      },
      onError: (error, { name }) => {
        errorToast({
          title: `Failed to create '${name}' environment`,
          description: (error.response?.data as ErrorResponse | null)?.error
            .message,
        });
      },
    },
    {
      defaultValues: {
        name: "",
      },
    },
  );

  const [showCreateForm, setShowCreateForm] = useState(false);

  return !showCreateForm ? (
    <Layout px={0}>
      <Box>
        <Button variant="outline" onClick={() => setShowCreateForm((c) => !c)}>
          New environment
        </Button>
      </Box>
    </Layout>
  ) : (
    <FormProvider {...form}>
      <EnvironmentForm
        submitLabel="Create"
        onClose={() => {
          setShowCreateForm(false);
          form.reset();
        }}
        onSubmit={handleSubmit}
      />
    </FormProvider>
  );
}

export default function Environments() {
  const envQuery = useEnvironments();
  const { env } = useEnvironment();

  return (
    <Flex direction="column" gap={4} maxW="wideForm">
      <Restricted alertStyle="callout" role="admin">
        <Flex direction="column" gap={2}>
          <Heading as="h3" fontSize="md">
            Environments
          </Heading>
          <Flex direction="column" gap={2} mb={4}>
            <Text color="dimmed" fontSize="sm" maxWidth="2xl">
              Environments in Reflag allow you to contain flag data to a
              specific environment and manage flags throughout your development
              lifecycle, from local development through to staging and
              production.{" "}
              <Link
                href="https://docs.reflag.com/product-handbook/creating-and-managing-apps/environments"
                target="_blank"
              >
                Learn more
              </Link>
            </Text>
            <Text color="dimmed" fontSize="sm" maxWidth="2xl">
              You can find the publishable and secret keys for each environment
              in{" "}
              <Link
                as={RouterLink}
                to={GlobalSettingsUrl(env, "org-api-access")}
              >
                SDK & API keys
              </Link>
            </Text>
          </Flex>
          <QueryDataGuard
            query={envQuery}
            render={({ nonProduction, production }) => (
              <>
                <EnvironmentsList
                  nonProduction={nonProduction}
                  production={production}
                />
                <CreateEnvironment />
              </>
            )}
          />
        </Flex>
      </Restricted>
    </Flex>
  );
}
